import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe
} from "firebase/firestore";
import type { ChecklistContext, ChecklistQuestion, ChecklistTemplate, ChecklistTemplateScope } from "../types/imobi";
import { db } from "./firebase";

export type SaveChecklistTemplateInput = {
  companyId: string;
  name: string;
  scope: ChecklistTemplateScope;
  context: ChecklistContext;
  assetGroup?: string;
  assetTypeId?: string;
  assetId?: string;
  questions: ChecklistQuestion[];
  returnStorageLocation?: string;
};

function mapChecklistTemplate(companyId: string, id: string, data: Record<string, unknown>): ChecklistTemplate {
  return {
    id,
    companyId,
    name: String(data.name ?? ""),
    scope: (data.scope ?? "geral") as ChecklistTemplateScope,
    // Templates created before "context" existed have none stored; devolução is the
    // flow that was actually in use, so that's the safe default for old data.
    context: data.context === "retirada" ? "retirada" : "devolucao",
    assetGroup: data.assetGroup ? String(data.assetGroup) : undefined,
    assetTypeId: data.assetTypeId ? String(data.assetTypeId) : undefined,
    assetId: data.assetId ? String(data.assetId) : undefined,
    questions: Array.isArray(data.questions) ? (data.questions as ChecklistQuestion[]) : [],
    returnStorageLocation: data.returnStorageLocation ? String(data.returnStorageLocation) : undefined,
    active: data.active !== false,
    createdAt: typeof data.createdAt === "string" ? data.createdAt : "",
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : undefined,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : undefined
  };
}

export function subscribeChecklistTemplates(companyId: string | undefined, onChange: (templates: ChecklistTemplate[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "checklistTemplates"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((templateDoc) => mapChecklistTemplate(companyId, templateDoc.id, templateDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function fetchChecklistTemplatesOnce(companyId: string): Promise<ChecklistTemplate[]> {
  if (!db || !companyId) {
    return [];
  }

  const snapshot = await getDocs(query(collection(db, "companies", companyId, "checklistTemplates"), orderBy("name")));
  return snapshot.docs.map((templateDoc) => mapChecklistTemplate(companyId, templateDoc.id, templateDoc.data()));
}

export async function createChecklistTemplate(input: SaveChecklistTemplateInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.name.trim()) {
    throw new Error("Informe o nome do checklist.");
  }

  if (!input.questions.length) {
    throw new Error("Adicione ao menos uma pergunta ao checklist.");
  }

  const templateRef = doc(collection(db, "companies", input.companyId, "checklistTemplates"));
  await setDoc(templateRef, {
    name: input.name.trim(),
    scope: input.scope,
    context: input.context,
    assetGroup: input.scope === "grupo" ? input.assetGroup ?? null : null,
    assetTypeId: input.scope === "tipo" ? input.assetTypeId ?? null : null,
    assetId: input.scope === "bem" ? input.assetId ?? null : null,
    questions: input.questions,
    returnStorageLocation: input.returnStorageLocation?.trim() || null,
    active: true,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  });
}

export async function updateChecklistTemplate(companyId: string, templateId: string, input: SaveChecklistTemplateInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!input.name.trim()) {
    throw new Error("Informe o nome do checklist.");
  }

  if (!input.questions.length) {
    throw new Error("Adicione ao menos uma pergunta ao checklist.");
  }

  await updateDoc(doc(db, "companies", companyId, "checklistTemplates", templateId), {
    name: input.name.trim(),
    scope: input.scope,
    context: input.context,
    assetGroup: input.scope === "grupo" ? input.assetGroup ?? null : null,
    assetTypeId: input.scope === "tipo" ? input.assetTypeId ?? null : null,
    assetId: input.scope === "bem" ? input.assetId ?? null : null,
    questions: input.questions,
    returnStorageLocation: input.returnStorageLocation?.trim() || null,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function setChecklistTemplateActive(companyId: string, templateId: string, active: boolean, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "checklistTemplates", templateId), {
    active,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
